/* ==========================================================================
   Fitted size selection — The Lille Björn
   assets/lb-fitted-size.js · pairs with snippets/lb-fitted-size-picker.liquid,
   snippets/lb-fitted-cart-line.liquid and assets/lb-fitted-size.css

   No size is ever chosen for the shopper, so there is nothing to confirm
   afterwards:

     · The size row starts empty. The add-to-cart button reads "Select a size"
       and stays tappable: a tap warms the row that is still open and moves
       focus there. No dialog, no message.
     · Choosing a size fills the button ("Add to cart · M"), writes the variant
       into the theme's form, and shows Buy it now in space already reserved.
     · A measurement saved in the Fitting Room chooses the size on arrival and
       says so: "Fitted to Bruno". A different pick is respected; the line reads
       "Bruno measured 34 cm. M would sit best." with one way back.
     · The bag repeats the size with its range and can change it in place.

   It drives the theme's own markup and never renders a dialog.
   ========================================================================== */
(function () {
  'use strict';

  /* Phase A — Quick Shop carries its own assets, because layout/theme.liquid is
     out of scope. SHTDialogQuickShop injects the section body (tags and all)
     into .js-dialog-body and replaces it on every open, so the stylesheet is
     moved into <head> where it survives, and any duplicate tags are dropped.
     This runs on every execution, before the re-entry guard below. */
  (function tidyAssets() {
    /* Match on the file, not on our own attribute: layout/theme.liquid may also
       load the stylesheet with {{ 'lb-fitted-size.css' | asset_url |
       stylesheet_tag }}, which carries no data-lbf-css. Keeping only the first
       copy means the layout's tag wins when it is there, and the section's is
       dropped instead of being hoisted next to it. */
    var keep = function (nodes) {
      for (var i = 0; i < nodes.length; i++) {
        if (i === 0) {
          if (nodes[0].parentNode !== document.head) document.head.appendChild(nodes[0]);
        } else if (nodes[i].parentNode) {
          nodes[i].parentNode.removeChild(nodes[i]);
        }
      }
    };
    var mine = function (sel, attr, file) {
      return Array.prototype.filter.call(document.querySelectorAll(sel), function (n) {
        return n.hasAttribute(attr) || (n.getAttribute(sel === 'link' ? 'href' : 'src') || '').indexOf(file) > -1;
      });
    };
    keep(mine('link', 'data-lbf-css', 'lb-fitted-size.css'));
    var tags = mine('script', 'data-lbf-js', 'lb-fitted-size.js');
    for (var j = 1; j < tags.length; j++) {
      if (tags[j].parentNode) tags[j].parentNode.removeChild(tags[j]);
    }
  })();

  /* Executed again by a later open: the listeners, the observer and the pickers
     are all still live, so there is nothing to set up. Rescan anyway — it is
     idempotent (pickers is a WeakMap keyed on the element) and it removes any
     dependence on the observer having fired first. */
  if (window.LBFitted) {
    if (typeof window.LBFitted.scan === 'function') window.LBFitted.scan(document);
    return;
  }

  var FR_KEY = 'lillebjorn_fr_state_v2';
  /* Sizes and measurements come from assets/lillebjorn-fit-logic.js when that
     is on the page (Size Guide, Fit Finder). These tables are the same values,
     for the pages that don't load it. */
  var TABLES = {
    collar: {
      key: 'neck', measure: 'neck',
      sizes: ['XXS', 'XS', 'S', 'M', 'L', 'XL'],
      nodes: [17, 22, 27, 32, 37, 42, 47],
      hint: 'Measured around the neck, where the collar sits.'
    },
    harness: {
      key: 'chest', measure: 'chest',
      sizes: ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL'],
      nodes: [26, 30, 34, 38, 46, 54, 64, 76],
      hint: 'Measured around the chest, just behind the front legs.'
    }
  };
  var TIE_CM = 0.127;

  var config = window.LBFittedConfig || {};
  config = {
    findTheFitSelector: config.findTheFitSelector || '.lb-ff-trigger-btn, .lillebjorn-trigger-button, [data-lb-ff-open]',
    calledMs: config.calledMs || 1600,
    // When a size change in the bag can't be followed by fresh drawer markup,
    // reload rather than leave the theme's own "Size:" line out of date.
    onCartFallback: config.onCartFallback || function () { window.location.reload(); },
    cartRoot: (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
  };

  /* ── Small helpers ─────────────────────────────────────────────────────── */
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; });
  }
  function parseJSON(text) { try { return JSON.parse(text); } catch (e) { return null; } }
  function emit(node, name, detail) {
    try { node.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail })); } catch (e) {}
  }
  function readFittingRoom() {
    try {
      var s = JSON.parse(localStorage.getItem(FR_KEY) || 'null');
      return s && typeof s === 'object' ? s : null;
    } catch (e) { return null; }
  }
  function dogName(fr) {
    var n = fr && fr.dogName ? String(fr.dogName).trim() : '';
    return n ? n.replace(/\s+/g, ' ').slice(0, 24) : '';
  }
  /* The size a measurement lands in, or null when there isn't one to trust:
     no tape measurement, an unresolved boundary, or the custom-size zone. */
  function measuredSize(part, fr) {
    var t = TABLES[part];
    if (!t || !fr) return null;
    var cm = fr[t.key];
    if (typeof cm !== 'number' || fr[t.key + 'Source'] !== 'measured') return null;
    var choice = fr[t.key + 'BoundaryChoice'];
    var FL = window.LillebjornFitLogic;
    if (FL && FL.computeSizeLetter && FL.boundaryTie && FL.MEASURE_META && FL.MEASURE_META[t.key]) {
      var meta = FL.MEASURE_META[t.key];
      if (FL.boundaryTie(cm, meta.nodes, meta.sizes) && !choice) return null;
      return FL.computeSizeLetter(t.key, cm, choice);
    }
    for (var i = 1; i < t.nodes.length - 1; i++) {
      if (Math.abs(cm - t.nodes[i]) <= TIE_CM) return choice ? (choice === 'loose' ? t.sizes[i - 1] : t.sizes[i]) : null;
    }
    if (cm >= t.nodes[t.nodes.length - 1]) return null;
    for (var j = 0; j < t.nodes.length - 1; j++) if (cm < t.nodes[j + 1]) return t.sizes[j];
    return null;
  }
  function measuredCm(part, fr) {
    var t = TABLES[part];
    return t && fr && typeof fr[t.key] === 'number' ? fr[t.key] : null;
  }
  /* The store's own conversion — see assets/lillebjorn-size-guide.js cmToIn()
     and assets/lillebjorn-fitting-room.js inDisp(). One decimal, so the numbers
     here are the same numbers the Size Guide prints. */
  function cmToIn(cm) { return Math.round(cm / 2.54 * 10) / 10; }

  function rangeOf(part, value) {
    var t = TABLES[part];
    var i = t ? t.sizes.indexOf(String(value).toUpperCase()) : -1;
    return i < 0 ? null : { from: t.nodes[i], to: t.nodes[i + 1], measure: t.measure };
  }
  function rangeText(part, value) {
    var r = rangeOf(part, value);
    return r ? r.measure + ' <span class="lbf__num">' + r.from + '–' + r.to + ' cm / ' +
      cmToIn(r.from) + '–' + cmToIn(r.to) + ' in</span>' : '';
  }
  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  /* Markup exactly as the browser reads it back from innerHTML. A label is
     only rewritten when it differs, and the label guard repaints on every
     write — so a string the browser serialises differently ("&", a quote,
     an attribute) would otherwise be rewritten forever. */
  var canonBox = null;
  function canon(html) {
    canonBox = canonBox || document.createElement('div');
    canonBox.innerHTML = html;
    return canonBox.innerHTML;
  }
  /* A size is a <button role="radio"> on the product page and a native radio
     (the Atelier chip) in Quick Shop. */
  function isRadio(pick) { return pick.tagName === 'INPUT'; }
  function isOn(pick) { return isRadio(pick) ? pick.checked : pick.getAttribute('aria-checked') === 'true'; }
  function checkedPick(row) {
    var picks = row ? row.querySelectorAll('[data-lbf-pick]') : [];
    for (var i = 0; i < picks.length; i++) if (isOn(picks[i])) return picks[i];
    return null;
  }

  /* ── The picker ────────────────────────────────────────────────────────── */
  function Picker(el) {
    this.el = el;
    this.data = parseJSON((el.querySelector('[data-lbf-data]') || {}).textContent || '') || { options: [], variants: [] };
    this.options = this.data.options || [];
    this.variants = this.data.variants || [];
    this.rows = Array.prototype.slice.call(el.querySelectorAll('[data-lbf-row]'));
    this.formId = el.getAttribute('data-form') || '';
    this.atelier = el.getAttribute('data-look') === 'atelier';
    this.text = this.data.text || {};
    this.sel = [];
    this.src = [];
    this.calledTimer = null;
    this.init();
  }

  Picker.prototype.form = function () {
    return (this.formId && document.getElementById(this.formId)) || this.el.closest('form') ||
      (this.el.closest('.shopify-section, .js-dialog-body, body') || document).querySelector('form[action*="/cart/add"]');
  };
  // Buttons and Buy it now that belong to this picker: named by form id, or,
  // failing that, in the same section or Quick Shop body.
  Picker.prototype.scope = function () {
    return this.el.closest('.js-dialog-body, .shopify-section, [data-lbf-scope]') || document.body;
  };
  Picker.prototype.owns = function (node) {
    var id = node.getAttribute('data-lbf-form');
    if (id) return id === this.formId;
    var f = this.form();
    return (f && f.contains(node)) || this.scope().contains(node);
  };
  Picker.prototype.buttons = function () {
    return Array.prototype.filter.call(document.querySelectorAll('[data-lbf-atc]'), this.owns, this);
  };
  Picker.prototype.express = function () {
    return Array.prototype.filter.call(document.querySelectorAll('[data-lbf-express]'), this.owns, this);
  };
  /* The theme's sticky bars on the product page (bundle.js pdpStickyAtc): a
     button that only forwards its click to ours, and one <select> per option
     that mirrors every tile tap. Neither is ours to restyle, but the button
     must say what ours says and the selects must hold what the shopper chose. */
  var STICKY_BTN = '.pdp-sticky-atc-mobile .js-product-form-submit-btn, .pdp-sticky-atc-desktop .js-product-form-submit-btn';
  var STICKY_SELECT = '.pdp-sticky-atc-mobile select.js-variant-select-item, .pdp-sticky-atc-desktop select.js-variant-select-item';
  Picker.prototype.stickyButtons = function () {
    return this.atelier ? [] : Array.prototype.slice.call(this.scope().querySelectorAll(STICKY_BTN));
  };
  // The option a mirrored <select> stands for: by its name, options[Collar size].
  Picker.prototype.positionOf = function (select) {
    for (var i = 0; i < this.options.length; i++) {
      if (select.name === 'options[' + this.options[i].name + ']') return i;
    }
    var host = select.closest('sht-variant-selects');
    return host ? Array.prototype.indexOf.call(host.querySelectorAll('.js-variant-select-item'), select) : -1;
  };
  Picker.prototype.syncSticky = function () {
    if (this.atelier) return;
    var self = this;
    Array.prototype.forEach.call(this.scope().querySelectorAll(STICKY_SELECT), function (s) {
      var v = self.sel[self.positionOf(s)];
      if (v && s.value !== v) s.value = v;        // no event: this is bookkeeping, not a choice
    });
  };

  /* PDP only. A tile tap reaches the theme through bundle.js (tile -> sticky
     <select> change -> sht-variant-selects -> price, stock, URL, gallery). A
     size chosen any other way — the Fitting Room preselect, the note's
     "Choose M" — never did, so the page kept showing the previous variant's
     price while the form held the new one (bundles are priced by size). Send
     the same change the tap would have sent, once per sticky bar. */
  Picker.prototype.tellTheme = function () {
    if (this.atelier || !this.variant()) return;
    var bars = this.scope().querySelectorAll('.pdp-sticky-atc-mobile, .pdp-sticky-atc-desktop');
    if (!bars.length) return;
    this.syncSticky();
    Array.prototype.forEach.call(bars, function (bar) {
      var s = bar.querySelector('select.js-variant-select-item');
      if (s) s.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  Picker.prototype.init = function () {
    var self = this;
    this.options.forEach(function (o, i) { self.sel[i] = null; self.src[i] = null; });

    // Nothing is chosen for the shopper — not a size, not a width, nothing.
    // Every row starts empty and the button waits for all of them.

    // A ?variant= link was a choice made somewhere else: honour it.
    var linked = this.data.selected && this.variantById(this.data.selected);
    if (linked) {
      linked.options.forEach(function (v, i) { self.sel[i] = v; self.src[i] = 'link'; });
    }

    // A measurement the shopper took in the Fitting Room chooses the size.
    var fr = readFittingRoom();
    this.options.forEach(function (o, i) {
      if (!o.part || self.src[i] === 'link') return;
      var m = measuredSize(o.part, fr);
      if (m && o.values.indexOf(m) > -1 && self.valueSelectable(i, m)) { self.sel[i] = m; self.src[i] = 'fitted'; }
    });

    this.el.addEventListener('click', function (e) {
      var pick = e.target.closest('[data-lbf-pick]');
      if (pick && !pick.disabled) { self.choose(pick.closest('[data-lbf-row]'), pick.getAttribute('data-value'), 'shopper', pick); return; }
      var swap = e.target.closest('[data-lbf-swap]');
      if (swap) { self.choose(swap.closest('[data-lbf-row]'), swap.getAttribute('data-value'), 'shopper'); self.tellTheme(); return; }
      var find = e.target.closest('[data-lbf-find]');
      if (find) { e.preventDefault(); self.openFitFinder(); }
    });
    this.el.addEventListener('keydown', function (e) { self.onKey(e); });

    this.render();
    if (!this.atelier) fitGuides(this.el);

    // A Fitting Room measurement completed the choice: show its price too.
    if (!this.atelier && this.src.indexOf('fitted') > -1 && this.variant()) {
      var tell = function () { if (self.el.isConnected) self.tellTheme(); };
      if (document.readyState === 'complete') setTimeout(tell, 0);
      else window.addEventListener('load', tell, { once: true });
    }

    // Quick Shop opens on the size row, not on the button: the first size still
    // open, or, when every size is set, the first size row's choice.
    if (this.el.closest('.js-dialog-body')) {
      setTimeout(function () {
        if (!self.el.isConnected || self.el.contains(document.activeElement)) return;
        var miss = self.missing();
        var row = miss.length
          ? self.rows.filter(function (r) { return +r.getAttribute('data-position') === miss[0]; })[0]
          : self.el.querySelector('[data-lbf-row][data-part]');
        if (!row) return;
        // A chosen size keeps the ring on its own chip; an empty row takes the
        // focus itself, so no chip is made to look selected.
        var start = checkedPick(row);
        if (!start) { row.setAttribute('tabindex', '-1'); start = row; }
        start.focus({ preventScroll: true });
      }, 80);
    }
  };

  Picker.prototype.variantById = function (id) {
    id = String(id);
    for (var i = 0; i < this.variants.length; i++) if (String(this.variants[i].id) === id) return this.variants[i];
    return null;
  };
  Picker.prototype.matches = function (values) {
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i], ok = true;
      for (var k = 0; k < values.length; k++) if (values[k] !== null && v.options[k] !== values[k]) { ok = false; break; }
      if (ok) return v;
    }
    return null;
  };
  // Is there an available variant with this value, given the other choices?
  Picker.prototype.valueSelectable = function (pos, value) {
    var probe = this.sel.slice();
    probe[pos] = value;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      if (!v.available) continue;
      var ok = true;
      for (var k = 0; k < probe.length; k++) if (probe[k] !== null && v.options[k] !== probe[k]) { ok = false; break; }
      if (ok) return true;
    }
    return false;
  };
  Picker.prototype.missing = function () {
    var out = [];
    for (var i = 0; i < this.options.length; i++) if (!this.sel[i]) out.push(i);
    return out;
  };
  Picker.prototype.variant = function () {
    if (this.sel.indexOf(null) > -1) return null;
    return this.matches(this.sel);
  };

  Picker.prototype.choose = function (row, value, source, node) {
    if (!row) return;
    var pos = +row.getAttribute('data-position');
    this.sel[pos] = value;
    this.src[pos] = source || 'shopper';
    this.render(node ? null : pos);
    emit(this.el, 'lb:fitted-change', { position: pos, value: value, variant: this.variant() });
  };

  Picker.prototype.onKey = function (e) {
    var pill = e.target.closest && e.target.closest('[data-lbf-pick]');
    if (!pill) return;
    var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (keys.indexOf(e.key) < 0) return;
    var pills = pill.closest('[data-lbf-pills]') || pill.parentNode;
    var group = Array.prototype.filter.call(pills.querySelectorAll('[data-lbf-pick]'), function (b) { return !b.disabled; });
    if (!group.length) return;
    var i = group.indexOf(pill);
    var next = e.key === 'Home' ? group[0]
      : e.key === 'End' ? group[group.length - 1]
      : group[(i + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1) + group.length) % group.length];
    e.preventDefault();
    next.focus();
    next.click();
  };

  Picker.prototype.openFitFinder = function () {
    var trigger = document.querySelector(config.findTheFitSelector);
    if (trigger) { trigger.click(); return; }
    emit(this.el, 'lb:fitted-find', {});
  };

  /* Paint everything the shopper reads from the current state. */
  Picker.prototype.render = function (focusPos) {
    var self = this, fr = readFittingRoom(), dog = dogName(fr);
    this.rows.forEach(function (row) {
      var pos = +row.getAttribute('data-position');
      var o = self.options[pos];
      var value = self.sel[pos];
      var part = o.part || '';
      var rec = part ? measuredSize(part, fr) : null;

      var label = row.querySelector('[data-lbf-label]');
      var shown = row.querySelector('[data-lbf-value]');
      if (shown) {
        // Atelier legend: the name stays, the choice is written beside it.
        if (shown.textContent !== (value || '')) shown.textContent = value || '';
      } else if (label) {
        var base = label.getAttribute('data-lbf-base') || label.textContent.trim();
        label.setAttribute('data-lbf-base', base);
        label.innerHTML = esc(base) + (value ? ' <b>' + esc(value) + '</b>' : '');
      }

      Array.prototype.forEach.call(row.querySelectorAll('[data-lbf-pick]'), function (pill) {
        var v = pill.getAttribute('data-value');
        var on = v === value;
        var selectable = self.valueSelectable(pos, v);
        if (isRadio(pill)) {
          // Native radios keep their own single tab stop.
          pill.checked = on;
          var chip = pill.closest('[data-lbf-chip]');
          if (chip) {
            // the theme's own state classes, so its stylesheet dresses the row
            chip.classList.toggle('product-option--sold-out', !selectable);
            chip.classList.toggle('product-option--unavailable', !selectable);
            chip.classList.toggle('product-option--active', on);
          }
        } else {
          pill.setAttribute('aria-checked', String(on));
          pill.tabIndex = on || (!value && pill === row.querySelector('[data-lbf-pick]:not([disabled])')) ? 0 : -1;
        }
        if (selectable) pill.removeAttribute('disabled');
        else pill.setAttribute('disabled', 'disabled');
        // a chip's dot lives in its <label>, a pill's inside the button
        var face = isRadio(pill) ? (pill.labels && pill.labels[0]) || pill.nextElementSibling : pill;
        var dot = face && face.querySelector('[data-lbf-dot]');
        if (dot) dot.hidden = !(rec && v === rec);
        var rg = part ? rangeOf(part, v) : null;
        var aria = v + (rg ? ', ' + TABLES[part].measure + ' ' + rg.from + ' to ' + rg.to + ' cm, ' +
          cmToIn(rg.from) + ' to ' + cmToIn(rg.to) + ' inches' : '');
        if (rec && v === rec && dog) aria += ', ' + dog + '’s size';
        if (!selectable) aria += ', sold out';
        pill.setAttribute('aria-label', aria);
      });

      var asking = row.classList.contains('is-called') && !value;
      var note = row.querySelector('[data-lbf-note]');
      if (note) {
        var html = asking
          ? '<span class="lbf__advice">' + esc(self.promptFor(pos)) + '</span>'
          : self.noteFor(pos, value, part, rec, dog);
        if (html !== '') {
          var hint = TABLES[part] && TABLES[part].hint;
          html = '<span class="lbf__now">' + html + '</span>' +
                 (hint ? '<span class="lbf__ghost">' + esc(hint) + '</span>' : '');
        }
        if (note.innerHTML !== html) {
          note.innerHTML = html;
          note.classList.remove('is-changed');
          void note.offsetWidth;            // restart the fade for the new line
          note.classList.add('is-changed');
        }
      }
    });

    var variant = this.variant();
    var form = this.form();
    if (form) {
      var input = form.querySelector('[name="id"]') || document.querySelector('[name="id"][form="' + (form.id || '') + '"]');
      if (input) {
        var next = variant ? String(variant.id) : '';
        if (input.value !== next) {
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    this.syncSticky();
    var relabelled = this.paintButtons(variant);
    var key = variant ? String(variant.id) : '';
    var moved = relabelled || key !== this.painted;
    this.painted = key;
    // Price before stock: Quick Shop's lb-quickshop.js checks, when the stock
    // line changes, that the price on screen belongs to the variant in the form.
    this.paintPrice(variant);
    this.paintStock(variant, moved);
    this.paintSummary(variant);

    if (typeof focusPos === 'number') {
      var row = this.rows.filter(function (r) { return +r.getAttribute('data-position') === focusPos; })[0];
      var sel = checkedPick(row);
      if (sel) sel.focus({ preventScroll: true });
    }
  };

  /* What to do, said plainly, while the row is asking. Returns '' for a row
     that is not being asked, so the note falls back to its usual line. */
  Picker.prototype.promptFor = function (pos) {
    var o = this.options[pos];
    var t = o.part && TABLES[o.part];
    if (t) return 'Choose the size that fits your dog’s ' + t.measure + '.';
    return 'Choose a ' + String(o.name).toLowerCase() + '.';
  };

  Picker.prototype.noteFor = function (pos, value, part, rec, dog) {
    if (!part) return '';
    var t = TABLES[part];
    if (!value) return t ? esc(t.hint) : '';
    var cm = measuredCm(part, readFittingRoom());
    // Plain text separators (" · "), not layout gaps, so a screen reader hears
    // "Fitted to Bruno, neck 34 cm" rather than "Brunoneck".
    if (rec && value === rec) {
      return '<span class="lbf__fitted">Fitted to ' + esc(dog || 'your dog') + '</span>' +
        (cm !== null ? ' · ' + t.measure + ' <span class="lbf__num">' + cm + ' cm / ' +
          cmToIn(cm) + ' in</span>' : '');
    }
    if (rec && value !== rec) {
      return '<span class="lbf__advice">' + esc(dog || 'Your dog') + ' measured ' + cm + ' cm / ' +
        cmToIn(cm) + ' in. ' + esc(rec) + ' would sit best.</span> ' +
        '<button type="button" data-lbf-swap data-value="' + esc(rec) + '">Choose ' + esc(rec) + '</button>';
    }
    return cap(rangeText(part, value));
  };

  /* Every label this button can ever show. Rendered as hidden ghosts in the
     same grid cell as the live label, so the cell reserves the widest of them
     and the button's measured width never depends on the chosen size. */
  Picker.prototype.labelGhosts = function () {
    if (this.ghosts != null) return this.ghosts;
    var cands = ['Select a size', 'Select sizes', 'Sold out'];
    this.options.forEach(function (o) {
      if (!o.part) cands.push('Select ' + esc(o.name.toLowerCase()));
    });
    var longest = this.options.filter(function (o) { return o.part; }).map(function (o) {
      var w = '';
      o.values.forEach(function (v) { if (String(v).length > w.length) w = String(v); });
      return w;
    });
    if (longest.length) {
      cands.push('Add to cart <span class="lbf__sep" aria-hidden="true"></span> ' +
                 longest.map(esc).join(' / '));
    }
    this.ghosts = cands.map(function (c) {
      return '<span class="lbf__ghost" aria-hidden="true">' + c + '</span>';
    }).join('');
    return this.ghosts;
  };

  /* The theme's own "Add to cart" label, restored in the add state. A page
     opened on a sold-out variant (?variant=) renders "Sold out" in it, which
     must not become the label of an available size picked afterwards. */
  function baseLabel(btn, slot) {
    var add = window.SHTLanguage && SHTLanguage.product && SHTLanguage.product.PRODUCT_ADD_TO_CART;
    if (btn.hasAttribute('disabled') && add) return ' ' + esc(add) + ' ';
    return slot.innerHTML;
  }

  Picker.prototype.paintButtons = function (variant) {
    var miss = this.missing(), self = this;
    var ready = !!variant && !miss.length;
    // Every variant sold out: "Select a size" would lead to a row with
    // nothing in it. Say so instead, as the theme did.
    var nothingLeft = this.variants.length > 0 && !this.variants.some(function (v) { return v.available !== false; });
    var soldOut = nothingLeft || (!miss.length && (!variant || variant.available === false));
    var text;
    if (nothingLeft) {
      text = 'Sold out';
    } else if (miss.length) {
      var sizesLeft = miss.filter(function (i) { return !!self.options[i].part; });
      if (sizesLeft.length > 1) text = 'Select sizes';
      else if (sizesLeft.length === 1) text = 'Select a size';
      // escaped: the label is compared with what the browser reads back, and
      // an unescaped "&" would never match, so every paint would rewrite it
      else text = 'Select ' + esc(this.options[miss[0]].name.toLowerCase());
    } else if (soldOut) {
      text = 'Sold out';
    } else {
      text = this.atelier
        ? 'Add to cart <span class="lbf__sep" aria-hidden="true"></span> ' +
          this.options.filter(function (o) { return o.part; }).map(function (o) { return esc(self.sel[o.position]); }).join(' / ')
        : null;   // the product page keeps the theme's own label
    }

    var relabelled = false;
    var ghosts = self.atelier ? self.labelGhosts() : '';
    this.buttons().forEach(function (btn) {
      var slot = btn.querySelector('.js-product-form-submit-btn-text') || btn;
      // the theme's own label, captured once, is what the add state restores
      if (btn.getAttribute('data-lbf-base') === null) btn.setAttribute('data-lbf-base', baseLabel(btn, slot));
      var body = text === null ? btn.getAttribute('data-lbf-base')
                               : canon('<span class="lbf__now">' + text + '</span>' + ghosts);
      if (slot.innerHTML !== body) { slot.innerHTML = body; relabelled = true; }
      btn.setAttribute('data-lbf-state', soldOut ? 'soldout' : miss.length ? 'select' : 'add');
      if (soldOut) btn.setAttribute('disabled', 'disabled');
      else btn.removeAttribute('disabled');
      btn.setAttribute('aria-describedby', btn.getAttribute('aria-describedby') || '');
      guardLabel(btn, self);
    });
    // The sticky bars' button: the same words, and nothing else of ours — no
    // state attribute, so none of our button styling reaches it.
    this.stickyButtons().forEach(function (btn) {
      // their words sit straight in the button, with no text span
      var slot = btn.querySelector('.js-product-form-submit-btn-text') || btn;
      if (btn.getAttribute('data-lbf-base') === null) btn.setAttribute('data-lbf-base', baseLabel(btn, slot));
      fitSticky(btn, slot, text);
      if (!soldOut) btn.removeAttribute('disabled');
      guardLabel(btn, self);
      if (stickyOwners.indexOf(self) < 0) stickyOwners.push(self);
      // the phone bar is display:none until the page scrolls: fit it when it appears
      if (!btn.__lbfSized && window.ResizeObserver) {
        btn.__lbfSized = new ResizeObserver(function () {
          if (btn.offsetWidth && self.el.isConnected) self.paintButtons(self.variant());
        });
        btn.__lbfSized.observe(btn);
      }
    });
    this.express().forEach(function (node) { node.setAttribute('data-lbf-ready', String(ready)); });
    return relabelled;
  };

  /* The sticky button is nowrap and sized by its words, so a longer label
     pushes the bar's dropdowns into the colour selector on a phone. Its box
     stays exactly the width "Add to cart" gives it: the longest prompt that
     fits is used ("Select collar width" → "Select width" → "Select"), and the
     box is held at that width so a shorter one does not shift the bar either.
     A shortened prompt still reads in full to a screen reader (aria-label).
     · The width is measured per viewport width and font state: the button's
       padding and type size change by breakpoint, and Roboto may still be
       loading on the first paint.
     · While the bar is hidden (display:none until the page scrolls) it keeps
       the theme's own label, so the box is already "Add to cart"-wide when it
       appears and fitting it never changes its size.
     · The choice is cached per label, so the guard's repaint writes nothing. */
  /* Product page: "Size guide · Find My Dog's Size" beside the option name. Each link stays on one
     line; when the pair does not fit beside the name they stack (CSS, [data-lbf-guides="stack"]),
     in the height the two wrapped lines took before. Decided by measuring, so a short label ("Size:")
     keeps the row exactly as it was. Re-measured whenever the row's width changes. */
  function fitGuides(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('.product-option__heading'), function (h) {
      if (!h.querySelector('.size-guide__btn, .lb-ff-trigger-btn')) return;
      var measure = function () {
        if (!h.isConnected || !h.clientWidth) return;
        // the width the row really has (theme layout, links free to wrap)…
        h.removeAttribute('data-lbf-guides');
        var have = h.getBoundingClientRect().width;
        // …against what name + links need on one line. The flex parents grow to fit
        // nowrap content, so compare with the width measured above, not clientWidth.
        h.setAttribute('data-lbf-guides', 'inline');
        h.setAttribute('data-lbf-measure', '');
        var need = Math.max(h.scrollWidth, h.getBoundingClientRect().width);
        h.removeAttribute('data-lbf-measure');
        h.setAttribute('data-lbf-guides', need > have + 1 ? 'stack' : 'inline');
      };
      measure();
      if (!h.__lbfGuides && window.ResizeObserver) {
        var lastW = h.clientWidth;
        h.__lbfGuides = new ResizeObserver(function () {
          if (h.clientWidth === lastW) return;
          lastW = h.clientWidth;
          measure();
        });
        h.__lbfGuides.observe(h);
      }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    });
  }

  function fitSticky(btn, slot, text) {
    var base = btn.getAttribute('data-lbf-base');
    var key = window.innerWidth + '|' + (document.fonts ? document.fonts.status : '');
    if (btn.__lbfKey !== key) { btn.__lbfKey = key; btn.__lbfFit = {}; btn.__lbfW = 0; }
    var restore = function () {
      btn.style.minWidth = '';
      if (slot.innerHTML !== base) slot.innerHTML = base;
      if (btn.hasAttribute('data-lbf-aria')) { btn.removeAttribute('aria-label'); btn.removeAttribute('data-lbf-aria'); }
    };
    if (text === null || !btn.offsetWidth) { restore(); return; }
    if (!btn.__lbfW) {
      restore();                                            // measure the theme's own label
      btn.__lbfW = btn.offsetWidth;
      btn.setAttribute('data-lbf-w', String(btn.__lbfW));
    }
    var w = btn.__lbfW;
    var body = btn.__lbfFit[text];
    if (!body) {
      var ladder = [text];
      if (/^Select /.test(text)) {                          // "Sold out" is never shortened
        var words = text.split(' ');
        if (words.length > 2) ladder.push('Select ' + words[words.length - 1]);
        ladder.push('Select');
      }
      btn.style.minWidth = '';
      body = canon('<span class="lbf__now">' + ladder[ladder.length - 1] + '</span>');
      for (var i = 0; i < ladder.length; i++) {
        var trial = canon('<span class="lbf__now">' + ladder[i] + '</span>');
        slot.innerHTML = trial;
        if (btn.offsetWidth <= w + 0.5) { body = trial; break; }
      }
      btn.__lbfFit[text] = body;
    }
    if (slot.innerHTML !== body) slot.innerHTML = body;
    btn.style.minWidth = w + 'px';
    var full = canon(text); canonBox.innerHTML = full;
    var spoken = canonBox.textContent;
    canonBox.innerHTML = body;
    if (canonBox.textContent !== spoken) {
      if (btn.getAttribute('aria-label') !== spoken) btn.setAttribute('aria-label', spoken);
      btn.setAttribute('data-lbf-aria', '');
    } else if (btn.hasAttribute('data-lbf-aria')) {
      btn.removeAttribute('aria-label'); btn.removeAttribute('data-lbf-aria');
    }
  }

  /* Refit every sticky button when the viewport width or the fonts change. */
  var stickyOwners = [];
  function refitSticky() {
    stickyOwners = stickyOwners.filter(function (p) { return p.el.isConnected; });
    stickyOwners.forEach(function (p) { p.paintButtons(p.variant()); });
  }
  (function () {
    var queued = false;
    window.addEventListener('resize', function () {
      if (queued) return;
      queued = true;
      (window.requestAnimationFrame || setTimeout)(function () { queued = false; refitSticky(); });
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refitSticky);
  })();

  /* While an option is still open the button's words are ours alone. The
     theme writes "Add to cart" into it from more than one place (its variant
     refresh, its sticky mirror), sometimes after a fetch; anything that does,
     while a choice is missing, is put back at once. */
  var guarded = typeof WeakSet === 'function' ? new WeakSet() : null;
  function guardLabel(btn, picker) {
    if (!guarded || guarded.has(btn) || !window.MutationObserver) return;
    guarded.add(btn);
    new MutationObserver(function () {
      if (picker.el.isConnected && picker.missing().length) picker.paintButtons(null);
    }).observe(btn, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  }

  /* The picker replaces the theme's own size radios, and with them the
     theme's refresh of price and stock. These repaint both from the data the
     snippet wrote, in the page's own markup and wording. */
  Picker.prototype.find = function (sel) {
    var scope = this.scope();
    return scope.querySelector(sel);
  };
  // The stock line stays hidden until every size is chosen, then speaks for
  // that variant only.
  // In Quick Shop the line is refilled on every change, as the theme's own
  // refresh does: that is what tells lb-quickshop.js to fit the dialog again
  // (a longer "Add to cart · M / S" may need the buttons stacked).
  Picker.prototype.paintStock = function (variant, moved) {
    var box = this.find('[data-lbf-stock]');
    if (!box) return;
    var atelier = this.atelier || !!box.querySelector('.lbqs__stock-in');
    var inner = atelier && box.querySelector('.lbqs__stock-in');
    if (!variant) {
      box.setAttribute('data-lbf-stock', 'pending');
      if (inner && moved && inner.textContent) inner.textContent = inner.textContent;
      return;
    }
    var state = variant.stock || '', text = variant.stockText || '';
    if (atelier) {
      if (!inner) { inner = document.createElement('span'); box.appendChild(inner); }
      var cls = 'lbqs__stock-in is-' + (state || 'in');
      if (inner.className !== cls) inner.className = cls;
      if (moved || inner.textContent !== text) inner.textContent = text;
    } else {
      var html = state ? '<span class="' + ({ in: 'product--instock color-success', low: 'product--lowstock', out: 'product--outstock color-danger' })[state] + '">' + text + '</span>' : '';
      if (box.innerHTML.trim() !== html) box.innerHTML = html;
    }
    box.setAttribute('data-lbf-stock', 'shown');
  };
  // Quick Shop (Atelier) price: follows the chosen variant, and carries its id
  // in data-variant as the theme's own refresh would.
  Picker.prototype.paintPrice = function (variant) {
    if (!variant || !variant.money) return;
    var box = this.find('.lbqs__price-in[data-variant]');
    if (!box || box.getAttribute('data-variant') === String(variant.id)) return;
    var t = this.text, sale = !!variant.compare;
    var html = '<span class="lbqs__sr">' + esc(sale ? t.sale || '' : t.regular || '') + '</span>';
    var amount = box.querySelector('.lbqs__amount');
    var now = amount && amount.querySelector('.lbqs__amount-now');
    if (now) now.textContent = variant.money;
    html += amount ? amount.outerHTML : '<span class="lbqs__amount"><span class="lbqs__amount-now">' + esc(variant.money) + '</span></span>';
    var cur = box.querySelector('.lbqs__cur');
    if (cur) html += cur.outerHTML;
    if (sale) html += '<span class="lbqs__sr">' + esc(t.regular || '') + '</span><s class="lbqs__was">' + esc(variant.compare) + '</s>';
    box.innerHTML = html;
    box.classList.toggle('is-sale', sale);
    box.classList.toggle('is-soldout', variant.available === false);
    box.setAttribute('data-variant', String(variant.id));
  };
  // The Quick Shop summary line ("M / 2cm"), as the theme writes it.
  Picker.prototype.paintSummary = function (variant) {
    var box = this.find('[id^="productQuickViewSelectedVariant-"]');
    if (!box) return;
    var text = variant ? variant.options.join(' / ') : '';
    if (box.textContent !== text) box.textContent = text;
  };

  /* Bring a row to the middle of its own scroller only: the page on a product
     page, the dialog in Quick Shop. scrollIntoView would also move every
     ancestor, including the page behind an open Quick Shop. */
  function scrollParent(node) {
    for (var n = node.parentElement; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
      var o = getComputedStyle(n).overflowY;
      if ((o === 'auto' || o === 'scroll') && n.scrollHeight > n.clientHeight + 1) return n;
    }
    return null;
  }
  function scrollToRow(row) {
    var smooth = !(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
    var box = scrollParent(row);
    var r = row.getBoundingClientRect();
    if (box) {
      var b = box.getBoundingClientRect();
      // already fully in view: leave it alone, so tapping again does not jiggle
      if (r.top >= b.top - 1 && r.bottom <= b.bottom + 1) return;
      var scale = box.offsetHeight ? b.height / box.offsetHeight : 1;      // transformed ancestors
      var delta = (r.top + r.height / 2 - (b.top + b.height / 2)) / (scale || 1);
      box.scrollTo({ top: box.scrollTop + delta, behavior: smooth ? 'smooth' : 'auto' });
      return;
    }
    // No scroller of its own. Inside the dialog that means the panel fits and
    // the row is on screen: scrolling the page behind the veil is never right.
    if (row.closest('.js-dialog-body, .lbqs-dialog, dialog, [role="dialog"]')) return;
    if (r.top >= 0 && r.bottom <= window.innerHeight) return;
    window.scrollTo({ top: window.pageYOffset + r.top + r.height / 2 - window.innerHeight / 2, behavior: smooth ? 'smooth' : 'auto' });
  }

  /* A tap on "Select a size": warm the row that is still open, and put the
     shopper in it. No message, no dialog. */
  Picker.prototype.call = function () {
    var miss = this.missing();
    if (!miss.length) return false;
    var row = this.rows.filter(function (r) { return +r.getAttribute('data-position') === miss[0]; })[0];
    if (!row) return true;
    var self = this;
    this.rows.forEach(function (r) { r.classList.remove('is-called'); });
    row.classList.add('is-called');
    clearTimeout(this.calledTimer);
    this.calledTimer = setTimeout(function () {
      row.classList.remove('is-called');
      self.render();                      // put the usual line back
    }, config.calledMs);
    this.render();                        // say what to do, now
    scrollToRow(row);
    // the row, not its first chip: ringing a chip would read as a choice
    var target = checkedPick(row);
    if (!target) { row.setAttribute('tabindex', '-1'); target = row; }
    target.focus({ preventScroll: true });
    emit(this.el, 'lb:fitted-called', { position: miss[0] });
    return true;
  };

  /* ── The bag ───────────────────────────────────────────────────────────── */
  function CartLine(el) {
    this.el = el;
    this.data = parseJSON((el.querySelector('[data-lbf-line-data]') || {}).textContent || '') || { options: [], variants: [] };
    this.key = el.getAttribute('data-key');
    this.variantId = el.getAttribute('data-variant');
    this.quantity = parseInt(el.getAttribute('data-quantity'), 10) || 1;
    this.note = el.querySelector('[data-lbf-line-note]');
    this.changeBtn = el.querySelector('[data-lbf-line-change]');
    this.edit = el.querySelector('[data-lbf-line-edit]');
    this.error = el.querySelector('[data-lbf-line-error]');
    this.open = false;
    this.init();
  }
  CartLine.prototype.variant = function () {
    var id = String(this.variantId);
    for (var i = 0; i < this.data.variants.length; i++) if (String(this.data.variants[i].id) === id) return this.data.variants[i];
    return null;
  };
  CartLine.prototype.init = function () {
    var self = this;
    this.render();
    if (this.changeBtn) {
      this.changeBtn.addEventListener('click', function () {
        self.open = !self.open;
        self.changeBtn.textContent = self.open ? 'Done' : 'Change size';
        self.renderEdit();
        if (self.open) {
          var first = self.edit.querySelector('[data-lbf-pick]:not([disabled])');
          if (first) first.focus();
        }
      });
    }
    if (this.edit) {
      this.edit.addEventListener('click', function (e) {
        var pill = e.target.closest('[data-lbf-pick]');
        if (pill && !pill.disabled) self.swap(pill.getAttribute('data-position'), pill.getAttribute('data-value'));
      });
    }
  };
  CartLine.prototype.render = function () {
    var v = this.variant();
    if (!v || !this.note) return;
    var fr = readFittingRoom(), dog = dogName(fr);
    // The theme's line already names the size ("Size: M"); this adds only the
    // measurement it stands for, and the dog when it matches.
    var parts = this.data.options.filter(function (o) { return o.part; });
    var fitted = parts.map(function (o) { var rec = measuredSize(o.part, fr); return !!(rec && rec === v.options[o.position] && dog); });
    var allFitted = parts.length > 1 && fitted.every(Boolean);
    var bits = parts.map(function (o, i) {
      var value = v.options[o.position];
      var line = parts.length > 1 ? esc(cap(o.part)) + ' · ' + rangeText(o.part, value) : cap(rangeText(o.part, value));
      if (fitted[i] && !allFitted) line += ' · <span class="lbf__fitted">Fitted to ' + esc(dog) + '</span>';
      return '<span class="lbf-line__part">' + line + '</span>';
    });
    // A set fitted throughout says so once, after its pieces.
    if (allFitted) bits.push('<span class="lbf-line__part"><span class="lbf__fitted">Fitted to ' + esc(dog) + '</span></span>');
    this.note.innerHTML = bits.join('');
    if (this.changeBtn) this.changeBtn.hidden = false;
  };
  CartLine.prototype.renderEdit = function () {
    if (!this.edit) return;
    this.edit.hidden = !this.open;
    if (!this.open) { this.edit.innerHTML = ''; return; }
    var v = this.variant(), self = this;
    var fr = readFittingRoom();
    this.edit.innerHTML = this.data.options.filter(function (o) { return o.part; }).map(function (o) {
      var rec = measuredSize(o.part, fr);
      return '<div class="lbf__pills" role="radiogroup" aria-label="' + esc(o.name) + '">' + o.values.map(function (val) {
        var probe = v.options.slice();
        probe[o.position] = val;
        var ok = self.data.variants.some(function (x) {
          return x.available && x.options.every(function (y, k) { return y === probe[k]; });
        });
        return '<button type="button" class="lbf__pill" role="radio" aria-checked="' + (val === v.options[o.position]) + '"' +
          ' data-lbf-pick data-position="' + o.position + '" data-value="' + esc(val) + '"' + (ok ? '' : ' disabled') +
          ' aria-label="' + esc(val + (ok ? '' : ', sold out')) + '">' + esc(val) +
          '<span class="lbf__dot"' + (rec === val ? '' : ' hidden') + '></span></button>';
      }).join('') + '</div>';
    }).join('');
  };
  /* The theme's drawer only redraws from section HTML, so the swap asks for it
     in the same request (the pattern assets/lillebjorn-size-guide.js uses). */
  function drawerSectionId() {
    var live = document.querySelector('.cart-drawer__wrapper');
    var host = live && live.closest('[id^="shopify-section-"]');
    return host ? host.id.replace(/^shopify-section-/, '') : null;
  }
  function applyDrawer(html) {
    var live = document.querySelector('.cart-drawer__wrapper');
    if (!live || !html) return false;
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-drawer__wrapper');
    if (!fresh) return false;
    live.innerHTML = fresh.innerHTML;
    return true;
  }

  /* Shopify can't move a line to another variant, so this removes the line and
     adds the new one with the same properties and quantity. */
  CartLine.prototype.swap = function (position, value) {
    var v = this.variant();
    if (!v) return;
    var want = v.options.slice();
    want[+position] = value;
    var target = this.data.variants.filter(function (x) { return x.options.every(function (y, k) { return y === want[k]; }); })[0];
    if (!target || target.available === false) { this.fail('That size is sold out.'); return; }
    if (String(target.id) === String(v.id)) return;

    var self = this;
    this.el.classList.add('is-busy');
    if (this.error) this.error.textContent = '';
    var root = config.cartRoot;
    fetch(root + 'cart/change.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: this.key, quantity: 0 })
    }).then(function (r) {
      if (!r.ok) throw new Error('change');
      var body = { id: target.id, quantity: self.quantity };
      if (self.data.properties && Object.keys(self.data.properties).length) body.properties = self.data.properties;
      if (self.data.sellingPlan) body.selling_plan = self.data.sellingPlan;
      var section = drawerSectionId();
      if (section) { body.sections = [section]; body.sections_url = window.location.pathname; }
      return fetch(root + 'cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
    }).then(function (r) {
      return r.json().then(function (b) { if (!r.ok) throw new Error(b.description || 'add'); return b; });
    }).then(function (added) {
      var section = drawerSectionId();
      var fromAdd = section && added && added.sections && added.sections[section];
      if (applyDrawer(fromAdd)) return;
      var cartUrl = (window.routes && window.routes.cart_url) || (root + 'cart');
      return (section ? fetch(cartUrl + '?sections=' + section, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (json) { return applyDrawer(json && json[section]); })
        .catch(function () { return false; }) : Promise.resolve(false))
        .then(function (done) {
          if (!done) config.onCartFallback();
        });
    }).then(function () {
      emit(self.el, 'lb:fitted-cart-changed', { from: v.id, to: target.id });
      // Other listeners (the Free Gift modal's cart check) key off these.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }).catch(function (err) {
      self.el.classList.remove('is-busy');
      self.fail(String(err && err.message) === 'change' ? 'We could not update that line. Please try again.' : (err && err.message) || 'We could not update that line.');
    });
  };
  CartLine.prototype.fail = function (message) {
    if (this.error) this.error.textContent = message;
  };

  /* ── Wiring ────────────────────────────────────────────────────────────── */
  var pickers = new WeakMap();
  function scan(root) {
    (root || document).querySelectorAll('[data-lb-fitted]').forEach(function (el) {
      if (pickers.has(el)) return;
      pickers.set(el, new Picker(el));
    });
    (root || document).querySelectorAll('[data-lbf-line]').forEach(function (el) {
      if (el.__lbfLine) return;
      el.__lbfLine = new CartLine(el);
    });
  }
  // The picker a button belongs to: by form id first, then by section.
  function pickerFor(node) {
    var id = node.getAttribute('data-lbf-form');
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-lb-fitted]'));
    var el = (id && els.filter(function (x) { return x.getAttribute('data-form') === id; })[0]) || null;
    if (!el) {
      var host = node.closest('.js-dialog-body, .shopify-section, [data-lbf-scope]');
      el = host ? host.querySelector('[data-lb-fitted]') : null;
    }
    if (!el) return null;
    if (!pickers.has(el)) pickers.set(el, new Picker(el));
    return pickers.get(el);
  }

  /* The dynamic checkout button. On the product page Phase B adds no wrapper to
     it (its markup is the theme's, untouched), so it is found by walking up to
     the form and asking that form's picker. With a size missing the form would
     post an empty `id`, so the click is stopped and the row is warmed instead. */
  document.addEventListener('click', function (e) {
    var pay = e.target.closest && e.target.closest('.shopify-payment-button, sht-load-dyn-co-btn');
    if (!pay) return;
    var form = pay.closest('form') || document.querySelector('form[id="' + (pay.getAttribute('data-lbf-form') || '') + '"]');
    var host = pay.closest('.shopify-section, .main-product, .js-dialog-body, [data-lbf-scope]') || document;
    var el = host.querySelector('[data-lb-fitted]');
    if (!el || !pickers.has(el)) {
      if (!el) return;
      pickers.set(el, new Picker(el));
    }
    var picker = pickers.get(el);
    if (!picker.missing().length) return;      // complete: let the theme take it
    e.preventDefault();
    e.stopImmediatePropagation();
    picker.call();
  }, true);

  /* The sticky bars' mirror. bundle.js copies every tile tap into the bar's
     <select> and fires `change`; the theme's sht-variant-selects then resolves
     a variant from ALL of that bar's selects — including the options the
     shopper has not chosen, which a <select> always shows at their first
     value. It writes that variant into this form, rewrites the URL, and when
     its price fetch returns, relabels our button "Add to cart". On a kit that
     turned "Select collar width" into "Add to cart" after the size alone.
     So the change is heard here first: the picker takes the value (the bar's
     own dropdown is a choice too), and while anything is still open the theme
     never hears it. Once everything is chosen, the other selects are brought
     into line so the theme resolves the shopper's variant, not its defaults. */
  document.addEventListener('change', function (e) {
    var select = e.target;
    if (!select || !select.matches || !select.matches(STICKY_SELECT)) return;
    var host = select.closest('.shopify-section, [data-lbf-scope]');
    var el = host && host.querySelector('[data-lb-fitted]');
    if (!el) return;
    if (!pickers.has(el)) pickers.set(el, new Picker(el));
    var picker = pickers.get(el);
    if (picker.atelier) return;
    var pos = picker.positionOf(select);
    var row = picker.rows.filter(function (r) { return +r.getAttribute('data-position') === pos; })[0];
    if (row && picker.sel[pos] !== select.value && picker.options[pos].values.indexOf(select.value) > -1 &&
        picker.valueSelectable(pos, select.value)) {
      picker.choose(row, select.value, 'shopper', select);
    }
    if (picker.missing().length) {
      e.stopImmediatePropagation();
      return;
    }
    picker.syncSticky();
  }, true);

  // The add-to-cart button, wherever it sits: main form, sticky bar, Quick Shop.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-lbf-atc]');
    if (!btn || btn.disabled) return;
    var picker = pickerFor(btn);
    if (!picker) {
      // F4 — fail closed. The button says it needs a size and we cannot find the
      // picker that owns it, so the form must not post: `id` is empty and the
      // theme would ask the cart for nothing. Only reachable on a render
      // mismatch; blocking is always safer than adding the wrong thing.
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!picker.missing().length) return;      // a size is chosen: let the theme add it
    e.preventDefault();
    e.stopImmediatePropagation();
    picker.call();
  }, true);

  // Quick Shop injects its body after the fact, and the cart drawer re-renders
  // itself after every change.
  function watch() {
    if (!window.MutationObserver) return;
    var queued = false;
    new MutationObserver(function (list) {
      if (queued) return;
      for (var i = 0; i < list.length; i++) {
        for (var j = 0; j < list[i].addedNodes.length; j++) {
          var n = list[i].addedNodes[j];
          if (n.nodeType !== 1) continue;
          if (n.matches('[data-lb-fitted], [data-lbf-line]') || n.querySelector('[data-lb-fitted], [data-lbf-line]')) {
            queued = true;
            setTimeout(function () { queued = false; scan(document); }, 30);
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.LBFitted = {
    version: 1,
    config: config,
    scan: scan,
    measuredSize: measuredSize,
    pickerFor: pickerFor,
    tables: TABLES
  };

  function boot() { scan(document); watch(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
